import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createRentalTenant,
  deleteRentalTenant,
  fetchRentalTenants,
  updateRentalTenant,
  fetchRentalDetail,
  createAdminRentalTenant,
  deleteAdminRentalTenant,
  fetchAdminRentalTenants,
  updateAdminRentalTenant,
  fetchAdminRentalDetail
} from '../../Redux/Actions/actions';
import {
  RENTAL_TYPE,
  useRentalType,
  getDetailPath,
  typeLabel
} from '../RentalManagement/utils/rentalTypeHelpers';
import {
  FaArrowLeft,
  FaPlus,
  FaTrash,
  FaEdit,
  FaUser,
  FaUsers
} from 'react-icons/fa';
import style from './RentalTenants.module.css';

const defaultForm = {
  fullName: '',
  dni: '',
  cuit: '',
  email: '',
  phone: '',
  tenantType: 'main',
  occupation: '',
  employer: '',
  monthlyIncome: '',
  documentsText: '',
  guarantorFullName: '',
  guarantorDni: '',
  guarantorRelationship: '',
  guarantorPhone: ''
};

const tenantTypeLabels = {
  main: 'Inquilino principal',
  'co-tenant': 'Co-inquilino',
  guarantor: 'Garante'
};

const RentalTenants = () => {
  const { contractId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const type = useRentalType();
  const isAdmin = type === RENTAL_TYPE.ADMIN;

  const {
    rentalTenants = [],
    adminRentalTenants = [],
    rentalsLoading,
    adminRentalsLoading,
    rentalsError,
    adminRentalsError,
    rentalDetail,
    adminRentalDetail
  } = useSelector((state) => state || {});

  const tenants = isAdmin ? adminRentalTenants : rentalTenants;
  const loadingState = isAdmin ? adminRentalsLoading : rentalsLoading;
  const errorState = isAdmin ? adminRentalsError : rentalsError;

  const [formData, setFormData] = useState(defaultForm);
  const [editingTenant, setEditingTenant] = useState(null);

  const contractInfo = useMemo(() => {
    const detail = isAdmin ? adminRentalDetail : rentalDetail;
    if (!detail) return null;
    if (isAdmin) {
      return {
        id: detail.id,
        street: detail.property?.street || detail.propertyStreet,
        number: detail.property?.number || detail.propertyNumber
      };
    }
    return detail.rental || detail;
  }, [isAdmin, adminRentalDetail, rentalDetail]);

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchAdminRentalTenants(contractId));
      dispatch(fetchAdminRentalDetail(contractId));
    } else {
      dispatch(fetchRentalTenants(contractId));
      dispatch(fetchRentalDetail(contractId));
    }
  }, [dispatch, contractId, isAdmin]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData(defaultForm);
    setEditingTenant(null);
  };

  const buildPayload = () => {
    const documents = formData.documentsText
      ? formData.documentsText
          .split(',')
          .map((d) => d.trim())
          .filter(Boolean)
      : [];

    const guarantorData = formData.guarantorFullName
      ? {
          fullName: formData.guarantorFullName,
          dni: formData.guarantorDni,
          relationship: formData.guarantorRelationship,
          phone: formData.guarantorPhone
        }
      : undefined;

    return {
      fullName: formData.fullName,
      dni: formData.dni,
      cuit: formData.cuit,
      email: formData.email,
      phone: formData.phone,
      tenantType: formData.tenantType,
      occupation: formData.occupation,
      employer: formData.employer,
      monthlyIncome: formData.monthlyIncome ? Number(formData.monthlyIncome) : undefined,
      documents,
      guarantorData
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = buildPayload();

      if (editingTenant) {
        if (isAdmin) {
          await dispatch(updateAdminRentalTenant(editingTenant.id, payload));
        } else {
          await dispatch(updateRentalTenant(editingTenant.id, payload));
        }
      } else if (isAdmin) {
        await dispatch(createAdminRentalTenant(contractId, payload));
      } else {
        await dispatch(createRentalTenant(contractId, payload));
      }

      if (isAdmin) {
        await dispatch(fetchAdminRentalTenants(contractId));
      } else {
        await dispatch(fetchRentalTenants(contractId));
      }
      resetForm();
    } catch (err) {
      console.error('Error al guardar inquilino:', err);
      alert('No se pudo guardar el inquilino.');
    }
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setFormData({
      fullName: tenant.fullName || '',
      dni: tenant.dni || '',
      cuit: tenant.cuit || '',
      email: tenant.email || '',
      phone: tenant.phone || '',
      tenantType: tenant.tenantType || 'main',
      occupation: tenant.occupation || '',
      employer: tenant.employer || '',
      monthlyIncome: tenant.monthlyIncome || '',
      documentsText: (tenant.documents || []).join(', '),
      guarantorFullName: tenant.guarantorData?.fullName || '',
      guarantorDni: tenant.guarantorData?.dni || '',
      guarantorRelationship: tenant.guarantorData?.relationship || '',
      guarantorPhone: tenant.guarantorData?.phone || ''
    });
  };

  const handleDelete = async (tenantId) => {
    const confirmed = window.confirm('¿Eliminar este inquilino?');
    if (!confirmed) return;
    try {
      if (isAdmin) {
        await dispatch(deleteAdminRentalTenant(tenantId));
        await dispatch(fetchAdminRentalTenants(contractId));
      } else {
        await dispatch(deleteRentalTenant(tenantId));
        await dispatch(fetchRentalTenants(contractId));
      }
      if (editingTenant?.id === tenantId) {
        resetForm();
      }
    } catch (err) {
      console.error('Error al eliminar inquilino:', err);
      alert('No se pudo eliminar el inquilino.');
    }
  };

  return (
    <div className={style.container}>
      <div className={style.header}>
        <div className={style.headerLeft}>
          <button className={style.backBtn} onClick={() => navigate(getDetailPath(contractId, type))}>
            <FaArrowLeft /> Volver al contrato
          </button>
          <div>
            <h1 className={style.title}>
              <span
                style={{
                  display: 'inline-block',
                  padding: '2px 10px',
                  borderRadius: 999,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  background: isAdmin ? '#fef3c7' : '#dbeafe',
                  color: isAdmin ? '#92400e' : '#1e40af',
                  marginRight: 10,
                  verticalAlign: 'middle'
                }}
                title={typeLabel(type)}
              >
                {isAdmin ? 'Solo Admin' : 'Regular'}
              </span>
              Gestión de Inquilinos
            </h1>
            <p className={style.subtitle}>
              Contrato {contractInfo?.id || contractId} — {contractInfo?.street} {contractInfo?.number}
            </p>
          </div>
        </div>
      </div>

      <div className={style.grid}>
        <div className={style.formCard}>
          <div className={style.cardHeader}>
            <h3>{editingTenant ? 'Editar inquilino' : 'Agregar inquilino'}</h3>
            {editingTenant && (
              <button className={style.linkBtn} onClick={resetForm}>
                Cancelar edición
              </button>
            )}
          </div>

          <form className={style.form} onSubmit={handleSubmit}>
            <div className={style.twoCols}>
              <div className={style.field}>
                <label>Nombre completo *</label>
                <input name="fullName" value={formData.fullName} onChange={handleInputChange} required />
              </div>
              <div className={style.field}>
                <label>Tipo</label>
                <select name="tenantType" value={formData.tenantType} onChange={handleInputChange}>
                  <option value="main">Inquilino principal</option>
                  <option value="co-tenant">Co-inquilino</option>
                  <option value="guarantor">Garante</option>
                </select>
              </div>
            </div>

            <div className={style.twoCols}>
              <div className={style.field}>
                <label>DNI</label>
                <input name="dni" value={formData.dni} onChange={handleInputChange} />
              </div>
              <div className={style.field}>
                <label>CUIT</label>
                <input name="cuit" value={formData.cuit} onChange={handleInputChange} />
              </div>
            </div>

            <div className={style.twoCols}>
              <div className={style.field}>
                <label>Email</label>
                <input name="email" value={formData.email} onChange={handleInputChange} type="email" />
              </div>
              <div className={style.field}>
                <label>Teléfono</label>
                <input name="phone" value={formData.phone} onChange={handleInputChange} />
              </div>
            </div>

            <div className={style.twoCols}>
              <div className={style.field}>
                <label>Ocupación</label>
                <input name="occupation" value={formData.occupation} onChange={handleInputChange} />
              </div>
              <div className={style.field}>
                <label>Empleador</label>
                <input name="employer" value={formData.employer} onChange={handleInputChange} />
              </div>
            </div>

            <div className={style.twoCols}>
              <div className={style.field}>
                <label>Ingreso mensual</label>
                <input
                  name="monthlyIncome"
                  value={formData.monthlyIncome}
                  onChange={handleInputChange}
                  type="number"
                  min="0"
                />
              </div>
              {/* <div className={style.field}>
                <label>Documentos (separados por coma)</label>
                <input
                  name="documentsText"
                  value={formData.documentsText}
                  onChange={handleInputChange}
                  placeholder="url-dni-frente.jpg, url-dni-dorso.jpg"
                />
              </div> */}
            </div>

            <div className={style.divider}>Datos del garante (opcional)</div>
            <div className={style.twoCols}>
              <div className={style.field}>
                <label>Nombre completo</label>
                <input name="guarantorFullName" value={formData.guarantorFullName} onChange={handleInputChange} />
              </div>
              <div className={style.field}>
                <label>DNI</label>
                <input name="guarantorDni" value={formData.guarantorDni} onChange={handleInputChange} />
              </div>
            </div>

            <div className={style.twoCols}>
              <div className={style.field}>
                <label>Relación</label>
                <input
                  name="guarantorRelationship"
                  value={formData.guarantorRelationship}
                  onChange={handleInputChange}
                  placeholder="Madre, Padre, etc"
                />
              </div>
              <div className={style.field}>
                <label>Teléfono</label>
                <input name="guarantorPhone" value={formData.guarantorPhone} onChange={handleInputChange} />
              </div>
            </div>

            <div className={style.actions}>
              <button type="button" className={style.secondaryBtn} onClick={resetForm}>
                Limpiar
              </button>
              <button type="submit" className={style.primaryBtn} disabled={loadingState}>
                <FaPlus /> {editingTenant ? 'Actualizar' : 'Agregar'} inquilino
              </button>
            </div>
          </form>
        </div>

        <div className={style.listCard}>
          <div className={style.cardHeader}>
            <h3>
              <FaUsers /> Inquilinos del contrato ({tenants.length})
            </h3>
            <button
              className={style.linkBtn}
              onClick={() =>
                dispatch(
                  isAdmin
                    ? fetchAdminRentalTenants(contractId)
                    : fetchRentalTenants(contractId)
                )
              }
            >
              Recargar
            </button>
          </div>

          {loadingState && <p className={style.helper}>Cargando...</p>}
          {errorState && <p className={style.error}>Error: {errorState}</p>}

          {tenants.length === 0 && !loadingState ? (
            <div className={style.empty}>
              <FaUser />
              <p>No hay inquilinos registrados.</p>
              <span>Agrega al titular y garantes para este contrato.</span>
            </div>
          ) : (
            <div className={style.tenantsGrid}>
              {tenants.map((tenant) => (
                <div key={tenant.id} className={style.tenantCard}>
                  <div className={style.tenantHeader}>
                    <div>
                      <p className={style.tenantName}>{tenant.fullName}</p>
                      <span className={style.badge}>{tenantTypeLabels[tenant.tenantType] || tenant.tenantType}</span>
                    </div>
                    <div className={style.cardActions}>
                      <button className={style.iconBtn} onClick={() => handleEdit(tenant)} title="Editar">
                        <FaEdit />
                      </button>
                      <button className={style.iconBtn} onClick={() => handleDelete(tenant.id)} title="Eliminar">
                        <FaTrash />
                      </button>
                    </div>
                  </div>

                  <div className={style.infoRow}><strong>DNI:</strong> {tenant.dni || '—'}</div>
                  <div className={style.infoRow}><strong>CUIT:</strong> {tenant.cuit || '—'}</div>
                  <div className={style.infoRow}><strong>Email:</strong> {tenant.email || '—'}</div>
                  <div className={style.infoRow}><strong>Teléfono:</strong> {tenant.phone || '—'}</div>
                  <div className={style.infoRow}><strong>Ocupación:</strong> {tenant.occupation || '—'}</div>
                  <div className={style.infoRow}><strong>Empleador:</strong> {tenant.employer || '—'}</div>
                  <div className={style.infoRow}><strong>Ingreso mensual:</strong> {tenant.monthlyIncome || '—'}</div>
                  {tenant.documents?.length > 0 && (
                    <div className={style.infoRow}>
                      <strong>Documentos:</strong> {tenant.documents.join(', ')}
                    </div>
                  )}
                  {tenant.guarantorData && (
                    <div className={style.guarantorBox}>
                      <p><strong>Garante:</strong> {tenant.guarantorData.fullName}</p>
                      <p><strong>DNI:</strong> {tenant.guarantorData.dni}</p>
                      <p><strong>Relación:</strong> {tenant.guarantorData.relationship}</p>
                      <p><strong>Teléfono:</strong> {tenant.guarantorData.phone}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RentalTenants;
